import React, { PureComponent, SyntheticEvent } from 'react';
import { connect, MapStateToProps, MapDispatchToProps } from 'react-redux';
import styled from 'styled-components';
import { Typography } from '@inplayer-org/inplayer-ui';

// actions
import {
  createCountrySet as createCountrySetAction,
  loadCountrySets as loadCountrySetsAction,
  loadCountries as loadCountriesAction,
  deleteCountrySet as deleteCountrySetAction,
  updateCountrySet as updateCountrySetAction,
  resetCountries as resetCountriesAction,
  // types
  CreateCountrySetPayload,
  UpdateCountrySetPayload,
} from 'ducks/merchantDucks/restrictions/actions';
import { openModal as openModalAction } from 'ducks/merchantDucks/modal/actions';

// Types
import RestrictionsState, { Country, Region, City } from 'ducks/merchantDucks/restrictions/types';
import RootState from 'ducks/RootState';
import { Item } from 'components/BoxItem';
import { ModalNames } from 'ducks/merchantDucks/modal/types';

// Utils
import countries from 'data/countries';
import orderBy from 'lodash/orderBy';
import transformDataForMultiselect from 'utils/transformDataForMultiselect';
import media from 'utils/mediaQueries';

// Components
import GeoRestrictionsItem from 'pages/MerchantPages/Settings/Auth/GeoRestrictions/components/GeoRestrictionItem';
import SetCountryModal from 'pages/MerchantPages/Settings/Auth/GeoRestrictions/components/modals/SetCountryModal';
import ConfirmModal from 'components/Modals/ConfirmModal';
import MainHolder from 'components/MainHolder';
import SubheaderWithBorder from 'components/SubheaderWithBorder';
import SetDefaultCountryModal from 'pages/MerchantPages/Settings/Auth/GeoRestrictions/components/modals/SetDefaultCountryModal';
import ContentHolder from 'components/ContentHolder';
import BoxContainer from 'components/BoxContainer';
import BoxItemDefaultGrid from 'components/BoxItemDefaultGrid';
import TourGuideWrapper from 'components/TourGuide';
import { BlueTypography } from 'components/SharedStyledElements';

// constants
import Rules from 'restrictions/roles/constants/rules';

// data
import usStates from 'data/usStates';

export const DescriptionContainer = styled.div`
  width: 100%;
  margin: 0 auto;
  padding-bottom: 2rem;
`;

const GeoRestrictionsItemGrid = styled(BoxItemDefaultGrid)`
  grid-template-columns: repeat(3, minmax(31%, 1fr));

  ${media.customMaxWidth(1200)} {
    grid-template-columns: repeat(2, minmax(48%, 1fr));
  }
`;

interface StateProps {
  restrictions: RestrictionsState;
  merchantId: number;
  roleRestrictions: Rules[];
}

interface DispatchProps {
  createCountrySet: (data: CreateCountrySetPayload) => any;
  loadCountrySets: () => any;
  loadCountries: (countrySetId: number | string) => any;
  deleteCountrySet: (countrySetId: string | number) => any;
  updateCountrySet: (data: UpdateCountrySetPayload) => any;
  resetCountries: () => any;
  openModal: (modalName: ModalNames) => void;
}

interface OwnProps {}

type Props = StateProps & DispatchProps & OwnProps;

interface State {
  type: string;
  id: string | number;
  title: string;
  listType: string;
  global: number;
  selectedModalCountries: string[];
  selectedModalStates: string[];
  selectedModalCities: City[];
}

interface StatesType {
  name: string;
  stateCode?: string;
  isoCode?: string;
}

export class GeoRestrictions extends PureComponent<Props, State> {
  state = {
    type: '',
    id: '',
    title: '',
    listType: '',
    global: 0,
    selectedModalCountries: [],
    selectedModalStates: [],
    selectedModalCities: [],
  };

  componentDidMount() {
    const { loadCountrySets } = this.props;
    loadCountrySets();
  }

  handleNew = (type: string, boxItem?: Item) => (e: SyntheticEvent) => {
    e.preventDefault();

    const { loadCountries, resetCountries, openModal } = this.props;

    const { id = '', name: title = '', type: listType = 'blacklist', global = 0 } = boxItem || {};
    const newState = { type, id, title, listType, global };

    this.setState(newState);

    if (boxItem) {
      loadCountries(id);
    }

    resetCountries();
    openModal(ModalNames.Country);
  };

  handleDelete = () => {
    const { deleteCountrySet } = this.props;
    const { id } = this.state;
    deleteCountrySet(id);
  };

  handleDefault = (closeModal: () => void) => () => {
    const { updateCountrySet, merchantId } = this.props;
    const {
      id,
      title,
      listType,
      global,
      selectedModalCountries,
      selectedModalStates,
      selectedModalCities,
    } = this.state;

    updateCountrySet({
      merchantId,
      countrySetId: id,
      name: title,
      type: listType,
      global,
      countries: selectedModalCountries,
      regions: selectedModalStates,
      cities: selectedModalCities,
    });
    closeModal();
  };

  handleDefaultModal = (boxItem: Item) => (e: SyntheticEvent) => {
    e.preventDefault();
    const { openModal } = this.props;

    const modalCountries = boxItem?.countries?.map((option: Country) => option.country) || [];

    const modalStates = boxItem?.regions?.map((option: Region) => option.name) || [];

    const modalCities = boxItem?.cities || [];

    this.setState({
      selectedModalCountries: modalCountries,
      selectedModalStates: modalStates,
      selectedModalCities: modalCities,
      id: boxItem.id,
      title: boxItem.name,
      listType: boxItem.type,
      global: Number(!boxItem.global),
    });
    openModal(ModalNames.DefaultCountry);
  };

  handleDeleteModal = (id: string | number) => (e: SyntheticEvent) => {
    e.preventDefault();
    const { openModal } = this.props;

    this.setState({ id });
    openModal(ModalNames.GeoRestriction);
  };

  handleDuplicate = (type: string, boxItem: Item) => (e: SyntheticEvent) => {
    e.preventDefault();

    const { loadCountries, openModal } = this.props;

    this.setState({ title: '', type, listType: boxItem.type });

    loadCountries(boxItem.id);
    openModal(ModalNames.Country);
  };

  geoRestrictionItemBoxRender = (isReadOnly: boolean) => (item: Item) => (
    <GeoRestrictionsItem
      boxItem={item}
      handleDeleteModal={this.handleDeleteModal}
      handleNew={this.handleNew}
      handleDefaultModal={this.handleDefaultModal}
      handleDuplicate={this.handleDuplicate}
      isReadOnly={isReadOnly}
    />
  );

  hasRule = (rule: Rules) => {
    const { roleRestrictions } = this.props;
    return roleRestrictions.includes(rule);
  };

  render() {
    const { restrictions, merchantId, createCountrySet, updateCountrySet } = this.props;
    const { type, title, listType, global, id } = this.state;

    const countriesProps = restrictions.countries.map((country) => country.country);
    const regionsProps = restrictions.regions.map(({ name }) => name);
    const citiesOptions = restrictions.cities.map(({ name, region, country }) => {
      return { disabled: true, label: `${name}, ${region}`, value: name, region, country };
    });
    const citiesProps = restrictions.cities.map(({ name, region }) => `${name},${region}`);
    const options = countries.map(({ code, name }) => ({
      value: code,
      label: name,
    }));

    const usOptions = usStates.map(({ name, code }: any) => ({
      value: `${code}`,
      label: `${name}, ${code}`,
      disabled: true,
    }));
    const selected = transformDataForMultiselect(options, countriesProps);
    const selectedStates = transformDataForMultiselect(usOptions, [...regionsProps, ...citiesProps]);

    const orderedCollection = orderBy(restrictions.geoCollection, ['created_at'], ['desc']);
    const geoCollection = orderedCollection.map((item) => ({ item }));

    const isReadOnly = this.hasRule(Rules.READONLY_GEO_RESTRICTIONS);
    return (
      <>
        <TourGuideWrapper withSideNav />
        <MainHolder>
          <SubheaderWithBorder>
            <ContentHolder>
              <BlueTypography variant="h1">Geo-restrictions</BlueTypography>
            </ContentHolder>
          </SubheaderWithBorder>
          <BoxContainer
            itemTitle="country set"
            items={geoCollection}
            handleAddNew={this.handleNew('new')}
            boxItemRender={this.geoRestrictionItemBoxRender(isReadOnly)}
            customItemGrid={GeoRestrictionsItemGrid}
            isReadOnly={isReadOnly}
          >
            <DescriptionContainer>
              <Typography variant="p">
                Make your assets available only in certain countries. You can create a country set with allowed or
                restricted countries. For US, you can also be more specific and add states and cities.
              </Typography>
            </DescriptionContainer>
          </BoxContainer>
          <SetCountryModal
            type={type}
            id={id}
            title={title}
            listType={listType}
            global={global}
            merchantId={merchantId}
            countries={type === 'edit' || type === 'duplicate' ? selected : []}
            options={options}
            createCountrySet={createCountrySet}
            updateCountrySet={updateCountrySet}
            isReadOnly={isReadOnly}
            usOptions={usOptions}
            selectedStates={type === 'edit' || type === 'duplicate' ? selectedStates : []}
            selectedCities={citiesOptions}
          />
          <ConfirmModal
            tag="confirm_your_action_modal"
            modalName={ModalNames.GeoRestriction}
            handleConfirm={this.handleDelete}
            description="Once a country set is deleted, all assets where it is set will either inherit the default one, or not be
          location restricted."
            message="Are you sure you want to delete the country set?"
          />
          <SetDefaultCountryModal isDefault={global} handleDelete={this.handleDefault} />
        </MainHolder>
      </>
    );
  }
}

export const mapStateToProps: MapStateToProps<StateProps, OwnProps, RootState> = (state) => ({
  restrictions: state.restrictions,
  merchantId: state.auth.user.id,
  roleRestrictions: state.auth.user.roleRestrictions,
});

const mapDispatchToProps: MapDispatchToProps<DispatchProps, OwnProps> = {
  createCountrySet: createCountrySetAction,
  loadCountrySets: loadCountrySetsAction,
  loadCountries: loadCountriesAction,
  deleteCountrySet: deleteCountrySetAction,
  updateCountrySet: updateCountrySetAction,
  resetCountries: resetCountriesAction,
  openModal: openModalAction,
};

export default connect(mapStateToProps, mapDispatchToProps)(GeoRestrictions);
